import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import {
  Button,
  ExternalLink,
  Flex,
  Logo,
  Text,
  NavLinkWithPopover,
  NavIconWithPopover,
  Skeleton,
} from 'src/packages/core';
import {
  ProfileIcon,
  GlobeIcon,
  NotificationIcon,
  DownloadIcon,
  SearchIcon,
  NotificationNewIcon,
  ThemeDarkIcon,
  ThemeLightIcon,
} from 'src/packages/icons';
import { useDisclosure } from 'src/hooks/useDisclosure';
import { useKycRules } from 'src/hooks/useKycRules';

import { LanguagesModal } from './modals/LanguagesModal';
import { LoggedUserModal } from './modals/LoggedUserModal';
import { BurgerMenu } from './components/BurgerMenu';
import { AssetsPopoverContent } from './components/AssetsPopoverContent';
import { DownloadIconPopoverContent } from './components/DownloadIconPopoverContent';
import { NotificationIconPopoverContent } from './components/NotificationIconPopoverContent';
import { ProfileIconPopoverContent } from './components/ProfileIconPopoverContent';
import { SearchIconContent } from './components/SearchIconContent';
import { GlobeIconPopoverContent } from './components/GlobeIconPopoverContent';

import { HeaderProps, IconToExcludeType } from './Header.types';
import {
  HeaderContainer,
  HeaderLeftPart,
  MainNavWrapper,
  HeaderMobileIcons,
  HeaderRightPart,
} from './Header.styles';

import { MoreLinks, OrdersLinks, getTradeLinks, MarketsLinks, getBurgerMenuLinks } from './links';

import { goToUrl } from 'src/utils/go-to-url';
import { config } from 'src/config';
import { QuickSearchModal } from './modals/QuickSearchModal';
import { ThemeProvider } from 'src/packages/theme';

export const copyToClipboardDefault = async (value: string, message = 'Copied!') => {
  await navigator.clipboard.writeText(value);
  alert(message);
};

const Header: React.FC<HeaderProps> = ({
  hideMainNav,
  hideRightNav,
  excludeRightIcons,
  user,
  userLoading = false,
  logoTitle,
  quickSearchData,
  announcements,
  notifications,
  locale,
  assets,
  appTheme,
  logout,
  copyToClipboard,
  theme = 'dark',
  kycSettings,
}: HeaderProps) => {
  const contextTheme = useTheme();
  const { t } = useTranslation();

  const {
    isOpen: isLanguagesModalOpen,
    close: closeLanguagesModal,
    open: openLanguagesModal,
  } = useDisclosure(false);
  const {
    isOpen: isLoggedUserModalOpen,
    close: closeLoggedUserModal,
    open: openLoggedUserModal,
  } = useDisclosure(false);
  const {
    isOpen: isQuickSearchModalOpen,
    close: closeQuickSearchModal,
    open: openQuickSearchModal,
  } = useDisclosure(false);

  const {
    tradingSpotAllowed,
    tradingDerivativesAllowed,
    convertPlatformAllowed,
    earnPlatformAllowed,
    allowTrading,
    allowDeposit,
  } = useKycRules(kycSettings);

  const shouldHideIcon = useCallback(
    (icon: IconToExcludeType) => {
      return !!(excludeRightIcons && excludeRightIcons.includes(icon));
    },
    [excludeRightIcons]
  );

  const RenderHeaderButtons = useMemo(() => {
    if (userLoading) {
      return (
        <Flex alignItems={'center'} gap={6}>
          <Skeleton
            width={'67px'}
            height={'36px'}
            borderRadius={'6px'}
            theme={'dark'}
            withBorder={true}
          />
          <Skeleton width={'67px'} height={'22px'} theme={'dark'} withBorder={true} />
          <Skeleton width={'67px'} height={'22px'} theme={'dark'} withBorder={true} />
          <Skeleton width={'20px'} height={'24px'} theme={'dark'} withBorder={true} />
        </Flex>
      );
    }

    return !user ? (
      <Flex alignItems={'center'} justifyContent={'center'} gap={2}>
        <Button
          variant={'ghostSmall'}
          theme={'dark'}
          text={t('button.login')}
          minWidth={'90px'}
          onClick={() => {
            goToUrl(`${config.coinflareUrl}/login`);
          }}
        />
        <Button
          theme='dark'
          variant={'secondarySmall'}
          text={t('button.sign_up')}
          minWidth={'90px'}
          onClick={() => {
            goToUrl(`${config.coinflareUrl}/register`);
          }}
        />
      </Flex>
    ) : (
      <>
        {!hideRightNav && (
          <>
            {allowDeposit && (
              <Button
                theme={'dark'}
                variant={'secondaryMedium'}
                text={t('button.deposit')}
                onClick={() => {
                  goToUrl(`${config.coinflareUrl}/assets/spot/deposit`);
                }}
              />
            )}

            <NavLinkWithPopover
              title={t('header.menu.assets')}
              mainLink={`${config.coinflareUrl}/assets`}
              popoverCustomContent={
                <AssetsPopoverContent assets={assets} theme={theme} kycSettings={kycSettings} />
              }
              theme={theme}
            />

            <NavLinkWithPopover title={t('header.menu.orders')} links={OrdersLinks} theme={theme} />
          </>
        )}

        <NavIconWithPopover
          icon={ProfileIcon}
          popoverCustomContent={
            <ProfileIconPopoverContent
              theme={theme}
              user={user}
              logout={logout}
              copyToClipboard={copyToClipboard}
            />
          }
          theme={theme}
        />
      </>
    );
  }, [
    userLoading,
    user,
    t,
    hideRightNav,
    allowDeposit,
    assets,
    theme,
    kycSettings,
    logout,
    copyToClipboard,
  ]);

  const RenderHeaderMobileIcons = useMemo(() => {
    if (userLoading) {
      return (
        <HeaderMobileIcons>
          <Skeleton width={'20px'} height={'20px'} theme={'dark'} withBorder={true} />
          <Skeleton width={'20px'} height={'20px'} theme={'dark'} withBorder={true} />
        </HeaderMobileIcons>
      );
    }

    return (
      <HeaderMobileIcons>
        {quickSearchData && (
          <Flex onClick={openQuickSearchModal}>
            <SearchIcon size={20} color={contextTheme.colors.palette.white} />
          </Flex>
        )}

        {!!user && (
          <Flex onClick={openLoggedUserModal}>
            <ProfileIcon size={20} color={contextTheme.colors.palette.white} />
          </Flex>
        )}

        {!user && (
          <Button
            theme='dark'
            minWidth={'75px'}
            variant={'secondarySmall'}
            text={t('button.sign_up')}
            onClick={() => {
              goToUrl(`${config.coinflareUrl}/register`);
            }}
          />
        )}

        <ExternalLink link={config.notificationCenterUrl}>
          {notifications && notifications.unread > 0 ? (
            <NotificationNewIcon color={contextTheme.colors.palette.white} />
          ) : (
            <NotificationIcon color={contextTheme.colors.palette.white} />
          )}
        </ExternalLink>

        <BurgerMenu
          user={user}
          links={getBurgerMenuLinks(
            convertPlatformAllowed,
            allowTrading,
            tradingSpotAllowed,
            tradingDerivativesAllowed,
            earnPlatformAllowed
          )}
          openLanguagesModal={openLanguagesModal}
        />
      </HeaderMobileIcons>
    );
  }, [
    userLoading,
    quickSearchData,
    openQuickSearchModal,
    contextTheme.colors.palette.white,
    user,
    openLoggedUserModal,
    t,
    notifications,
    convertPlatformAllowed,
    allowTrading,
    tradingSpotAllowed,
    tradingDerivativesAllowed,
    earnPlatformAllowed,
    openLanguagesModal,
  ]);

  return (
    <ThemeProvider theme={theme} rtl={contextTheme.rtl}>
      <HeaderContainer>
        <LanguagesModal
          isOpen={isLanguagesModalOpen}
          onClose={closeLanguagesModal}
          locale={locale}
        />

        {!!user && (
          <LoggedUserModal
            isOpen={isLoggedUserModalOpen}
            onClose={closeLoggedUserModal}
            user={user}
            logout={logout}
            copyToClipboard={copyToClipboard}
            theme={theme}
            kycSettings={kycSettings}
          />
        )}

        {quickSearchData && (
          <QuickSearchModal
            isOpen={isQuickSearchModalOpen}
            onClose={closeQuickSearchModal}
            quickSearchData={quickSearchData}
            theme={theme}
            kycSettings={kycSettings}
          />
        )}

        <HeaderLeftPart>
          <Logo theme={'dark'} title={logoTitle} />

          {!hideMainNav && (
            <MainNavWrapper>
              <NavLinkWithPopover
                title={t('header.menu.markets')}
                links={MarketsLinks}
                theme={theme}
              />

              <NavLinkWithPopover
                title={t('header.menu.trade')}
                links={getTradeLinks(convertPlatformAllowed, tradingSpotAllowed, allowTrading)}
                theme={theme}
              />

              {tradingDerivativesAllowed && allowTrading && (
                <ExternalLink link={config.derivativesUrl}>
                  <Text
                    text={t('header.menu.derivatives')}
                    variant='p2'
                    color={contextTheme.colors.palette.white}
                  />
                </ExternalLink>
              )}

              {earnPlatformAllowed && (
                <ExternalLink link={config.earnUrl}>
                  <Text
                    text={t('header.menu.earn')}
                    variant='p2'
                    color={contextTheme.colors.palette.white}
                  />
                </ExternalLink>
              )}

              <ExternalLink link={`${config.coinflareUrl}/affiliate`}>
                <Text
                  text={t('header.menu.affiliate')}
                  variant='p2'
                  color={contextTheme.colors.palette.white}
                />
              </ExternalLink>

              <NavLinkWithPopover title={t('header.menu.more')} links={MoreLinks} theme={theme} />
            </MainNavWrapper>
          )}
        </HeaderLeftPart>

        <HeaderRightPart>
          {quickSearchData && (
            <NavIconWithPopover
              icon={SearchIcon}
              popoverCustomContent={
                <SearchIconContent
                  theme={theme}
                  quickSearchData={quickSearchData}
                  kycSettings={kycSettings}
                />
              }
              theme={theme}
            />
          )}

          {RenderHeaderButtons}

          {!shouldHideIcon('download') && (
            <NavIconWithPopover
              icon={DownloadIcon}
              popoverCustomContent={<DownloadIconPopoverContent theme={theme} />}
              theme={theme}
            />
          )}

          {!shouldHideIcon('notifications') && (
            <NavIconWithPopover
              icon={
                notifications && notifications.unread > 0 ? NotificationNewIcon : NotificationIcon
              }
              popoverCustomContent={
                <NotificationIconPopoverContent
                  theme={theme}
                  announcements={announcements}
                  notifications={notifications}
                />
              }
              theme={theme}
            />
          )}

          {!shouldHideIcon('language') && (
            <NavIconWithPopover
              icon={GlobeIcon}
              popoverCustomContent={<GlobeIconPopoverContent theme={theme} locale={locale} />}
              theme={theme}
            />
          )}
          {appTheme && (
            <Flex onClick={appTheme.changeTheme}>
              <NavIconWithPopover
                icon={appTheme.theme === 'dark' ? ThemeLightIcon : ThemeDarkIcon}
                noPopover={true}
                theme={theme}
              />
            </Flex>
          )}
        </HeaderRightPart>
        {RenderHeaderMobileIcons}
      </HeaderContainer>
    </ThemeProvider>
  );
};

export default Header;
